/**
 * Onboarding Wizard — Phase G.3.
 *
 * "Hire a New Team Member" — 5-step guided workflow.
 *   1. Basic Information
 *   2. Employment
 *   3. Role & Department (with smart defaults from Access Template)
 *   4. Login & Access
 *   5. Review + Create
 *
 * Backend contract: POST /api/team-members/onboard (atomic).
 * Real-time validation: GET /api/team-members/check-availability
 * ID preview: GET /api/team-members/employee-id-preview
 */
import { useEffect, useMemo, useState } from "react";
import {
  User, Briefcase, ShieldCheck, KeyRound, ClipboardCheck, ArrowRight, ArrowLeft,
  Check, X, Loader2, AlertCircle, Copy, ExternalLink, Building2, Star,
  Sparkles, Info,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useHotelContext } from "@/contexts/HotelContext";
import { toast } from "sonner";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/primitives";
import { DatePicker } from "@/components/ui/date-picker";

const STEPS = [
  { id: 1, key: "basic",       label: "Basic Information", icon: User },
  { id: 2, key: "employment",  label: "Employment",        icon: Briefcase },
  { id: 3, key: "role_dept",   label: "Role & Department", icon: ShieldCheck },
  { id: 4, key: "login",       label: "Login & Access",    icon: KeyRound },
  { id: 5, key: "review",      label: "Review",            icon: ClipboardCheck },
];

/* ── UI atoms ────────────────────────────────────────────────────────── */
function Field({ label, required, hint, error, children, testid }) {
  return (
    <div className="space-y-1" data-testid={testid}>
      <label className="text-[11px] uppercase tracking-[0.10em] text-hocp-textTertiary font-semibold">
        {label}{required && <span className="text-hocp-error ml-0.5">*</span>}
      </label>
      {children}
      {hint && !error && (
        <div className="text-[11px] text-hocp-textTertiary">{hint}</div>
      )}
      {error && (
        <div className="text-[11px] text-hocp-error flex items-center gap-1">
          <AlertCircle className="w-3 h-3"/> {error}
        </div>
      )}
    </div>
  );
}

function Stepper({ current, onJump, completed }) {
  return (
    <ol className="flex items-center gap-1 flex-wrap on-dark" data-testid="wizard-stepper">
      {STEPS.map((s, i) => {
        const done = completed.has(s.id);
        const active = current === s.id;
        return (
          <li key={s.id} className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onJump(s.id)}
              disabled={!done && !active}
              style={active ? { color: "#fff" } : undefined}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-[12px] font-medium transition
                ${active ? "bg-hocp-navy shadow-sm"
                          : done ? "bg-hocp-honeydew/25 text-hocp-textPrimary hover:bg-hocp-honeydew/40"
                                 : "text-hocp-textTertiary cursor-not-allowed"}`}
              data-testid={`wizard-step-${s.id}`}
            >
              <span
                style={active ? { color: "var(--brand-navy)" } : undefined}
                className={`w-5 h-5 rounded-full text-[11px] flex items-center justify-center font-bold
                  ${active ? "bg-white"
                            : done ? "bg-hocp-honeydew text-white"
                                    : "bg-hocp-alice text-hocp-textTertiary"}`}>
                {done && !active ? <Check className="w-3 h-3"/> : s.id}
              </span>
              <span>{s.label}</span>
            </button>
            {i < STEPS.length - 1 && (
              <span className="text-hocp-border">›</span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

/* ── Step 1 ─────────────────────────────────────────────────────────── */
function StepBasic({ data, set, errors, checkAvailability, empIdPreview }) {
  const [checking, setChecking] = useState({});
  const debounceCheck = (field, value) => {
    setChecking(c => ({ ...c, [field]: true }));
    return checkAvailability(field, value).finally(() =>
      setChecking(c => ({ ...c, [field]: false }))
    );
  };
  return (
    <div className="grid grid-cols-2 gap-4">
      <Field label="Full Name" required error={errors.name} testid="fld-name">
        <Input value={data.name} onChange={e => set({ name: e.target.value })}
                placeholder="e.g. Priya Sharma" data-testid="wiz-name"/>
      </Field>
      <Field label="Email" required error={errors.email}
              hint={data.emailStatus === "available" && "Available"}
              testid="fld-email">
        <div className="relative">
          <Input value={data.email} onChange={e => set({ email: e.target.value })}
                  onBlur={e => e.target.value && debounceCheck("email", e.target.value)}
                  placeholder="name@hotel.com" type="email"
                  data-testid="wiz-email"
                  className={data.emailStatus === "taken" ? "border-hocp-error" : ""}/>
          {checking.email && <Loader2 className="absolute right-2 top-2.5 w-4 h-4 animate-spin text-hocp-textTertiary"/>}
          {!checking.email && data.emailStatus === "available" && <Check className="absolute right-2 top-2.5 w-4 h-4 text-hocp-success"/>}
          {!checking.email && data.emailStatus === "taken" && <X className="absolute right-2 top-2.5 w-4 h-4 text-hocp-error"/>}
        </div>
      </Field>
      <Field label="Mobile Number" testid="fld-mobile">
        <div className="relative">
          <Input value={data.mobile} onChange={e => set({ mobile: e.target.value })}
                  onBlur={e => e.target.value && debounceCheck("mobile", e.target.value)}
                  placeholder="+91 98xxxxxx99" data-testid="wiz-mobile"
                  className={data.mobileStatus === "taken" ? "border-hocp-error" : ""}/>
          {checking.mobile && <Loader2 className="absolute right-2 top-2.5 w-4 h-4 animate-spin text-hocp-textTertiary"/>}
          {!checking.mobile && data.mobileStatus === "available" && <Check className="absolute right-2 top-2.5 w-4 h-4 text-hocp-success"/>}
          {!checking.mobile && data.mobileStatus === "taken" && <X className="absolute right-2 top-2.5 w-4 h-4 text-hocp-error"/>}
        </div>
      </Field>
      <Field label="Employee ID"
              hint={data.autoGenId ? `Auto: ${empIdPreview || "—"}` : "Manual override — requires permission"}
              error={errors.employee_id}
              testid="fld-empid">
        <div className="flex items-center gap-2">
          <Input
            value={data.autoGenId ? "" : data.employee_id}
            onChange={e => set({ employee_id: e.target.value })}
            disabled={data.autoGenId}
            placeholder={data.autoGenId ? (empIdPreview || "will be auto-generated") : "e.g. HOTEL-2026-00042"}
            className="flex-1 font-mono"
            data-testid="wiz-empid"
          />
          <label className="text-[12px] flex items-center gap-1.5 cursor-pointer shrink-0">
            <input type="checkbox" checked={data.autoGenId}
                    onChange={e => set({ autoGenId: e.target.checked, employee_id: "" })}
                    data-testid="wiz-empid-auto"/>
            <span>Auto</span>
          </label>
        </div>
      </Field>
      <Field label="Profile Photo URL" hint="Optional. Paste any image URL." testid="fld-photo">
        <Input value={data.photo_url} onChange={e => set({ photo_url: e.target.value })}
                placeholder="https://…" data-testid="wiz-photo"/>
      </Field>
    </div>
  );
}

/* ── Step 2 ─────────────────────────────────────────────────────────── */
function StepEmployment({ data, set, employmentTypes, hotelName, managers, errors }) {
  return (
    <div className="grid grid-cols-2 gap-4">
      <Field label="Employment Type" required testid="fld-etype">
        <Select value={data.employment_type} onValueChange={v => set({ employment_type: v })}>
          <SelectTrigger data-testid="wiz-etype"><SelectValue/></SelectTrigger>
          <SelectContent>
            {employmentTypes.map(t => (
              <SelectItem key={t.code} value={t.code}>{t.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Joining Date" testid="fld-joining">
        <DatePicker value={(data.joining_date || '').slice(0,10)} onChange={(_v) => set({ joining_date: _v })} data-testid="wiz-joining"/>
      </Field>
      <Field label="Designation" hint="e.g. Front Desk Agent" testid="fld-designation">
        <Input value={data.designation} onChange={e => set({ designation: e.target.value })}
                data-testid="wiz-designation"/>
      </Field>
      <Field label="Hotel" hint="New hires join the hotel you're signed in to" error={errors.primary_hotel_id} testid="fld-hotel">
        <div className="h-10 flex items-center rounded-md border border-hocp-border bg-hocp-surface/60 px-3 text-[13px] font-medium text-hocp-textPrimary"
             data-testid="wiz-hotel">
          {hotelName || <span className="text-hocp-textTertiary">No hotel selected — switch to a hotel first</span>}
        </div>
      </Field>
      <Field label="Reporting Manager" hint="Optional" testid="fld-manager">
        <Select value={data.reporting_manager_id || "__none__"}
                 onValueChange={v => set({ reporting_manager_id: v === "__none__" ? "" : v })}>
          <SelectTrigger data-testid="wiz-manager"><SelectValue placeholder="Choose manager (optional)"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">None</SelectItem>
            {managers.map(m => (
              <SelectItem key={m.id} value={m.id}>{m.name} · {m.designation || m.role_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
    </div>
  );
}

/* ── Step 3 — the smart-defaults step ───────────────────────────────── */
function StepRoleDept({ data, set, templates, departments, errors }) {
  const template = useMemo(
    () => templates.find(t => t.id === data.access_template_id),
    [templates, data.access_template_id]
  );

  const applyTemplateDefaults = (t) => {
    if (!t) return;
    // Suggest primary department from template's first default_department (code)
    const [firstCode] = t.default_departments || [];
    if (firstCode) {
      const dept = departments.find(d => d.code === firstCode
                                      && (!data.primary_hotel_id || d.hotel_id === data.primary_hotel_id));
      if (dept) {
        const existing = data.department_assignments.filter(a => a.department_id !== dept.id);
        set({
          department_assignments: [
            { department_id: dept.id, is_primary: true, reason: "access_template" },
            ...existing.map(a => ({ ...a, is_primary: false })),
          ],
        });
      }
    }
  };

  const onPickTemplate = (tid) => {
    set({ access_template_id: tid });
    const t = templates.find(x => x.id === tid);
    if (t && data.department_assignments.length === 0) applyTemplateDefaults(t);
  };

  const addAssignment = (dept, is_primary) => {
    if (!dept) return;
    if (data.department_assignments.some(a => a.department_id === dept.id)) {
      toast.info("Department already added.");
      return;
    }
    const next = data.department_assignments.map(a =>
      is_primary ? { ...a, is_primary: false } : a);
    next.push({ department_id: dept.id, is_primary,
                 reason: is_primary ? "hired" : "cross_training" });
    set({ department_assignments: next });
  };

  const removeAssignment = (idx) => {
    const next = data.department_assignments.filter((_, i) => i !== idx);
    // Ensure exactly one primary remains if list is non-empty
    if (next.length && !next.some(a => a.is_primary)) next[0].is_primary = true;
    set({ department_assignments: next });
  };

  const setPrimary = (idx) => {
    set({ department_assignments: data.department_assignments.map(
      (a, i) => ({ ...a, is_primary: i === idx })) });
  };

  const updateAssignment = (idx, patch) => {
    set({ department_assignments: data.department_assignments.map(
      (a, i) => i === idx ? { ...a, ...patch } : a) });
  };

  const availableDepts = departments.filter(d =>
    (!data.primary_hotel_id || d.hotel_id === data.primary_hotel_id)
    && !data.department_assignments.some(a => a.department_id === d.id));

  return (
    <div className="space-y-5">
      <div>
        <div className="text-[13px] font-semibold text-hocp-textPrimary mb-1">
          What role are you hiring for?
        </div>
        <Select value={data.access_template_id || ""} onValueChange={onPickTemplate}>
          <SelectTrigger data-testid="wiz-template"><SelectValue placeholder="Pick an access template…"/></SelectTrigger>
          <SelectContent>
            {templates.map(t => (
              <SelectItem key={t.id} value={t.id}>{t.name} · {t.platform_role}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {template && (
        <div className="rounded-xl bg-hocp-honeydew/10 border border-hocp-honeydew/40 p-4"
              data-testid="wiz-template-summary">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-hocp-honeydew"/>
            <div className="text-[13px] font-semibold text-hocp-textPrimary">Smart defaults from “{template.name}”</div>
          </div>
          <div className="text-[12px] text-hocp-textSecondary flex flex-wrap items-center gap-3">
            <span>Platform Role: <strong className="text-hocp-textPrimary">{template.platform_role}</strong></span>
            <span>{template.extra_permissions.length} extra permissions</span>
            {template.default_departments?.length > 0 && (
              <span>Suggested departments: <strong className="text-hocp-textPrimary">{template.default_departments.join(", ")}</strong></span>
            )}
          </div>
          <div className="mt-2">
            <label className="text-[12px] flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={data.apply_template_defaults}
                      onChange={e => set({ apply_template_defaults: e.target.checked })}
                      data-testid="wiz-apply-defaults"/>
              Auto-fill suggested departments if I don&apos;t choose any
            </label>
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-[13px] font-semibold text-hocp-textPrimary">Department Assignments</div>
            <div className="text-[11px] text-hocp-textTertiary">Exactly one primary. Temporary assignments can have an end date.</div>
          </div>
        </div>

        {data.department_assignments.length === 0 && (
          <div className="rounded-xl border border-dashed border-hocp-border p-6 text-center text-[12px] text-hocp-textSecondary">
            No department chosen yet. Add one below.
          </div>
        )}

        {data.department_assignments.length > 0 && (
          <div className="space-y-2" data-testid="wiz-assign-list">
            {data.department_assignments.map((a, idx) => {
              const dept = departments.find(d => d.id === a.department_id);
              return (
                <div key={idx} className="rounded-xl border border-hocp-border p-3 flex items-center gap-3"
                      data-testid={`wiz-assign-${idx}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-[13px] text-hocp-textPrimary">
                        {dept?.name || a.department_id}
                      </span>
                      {a.is_primary && (
                        <Badge tone="accent" size="sm">
                          <Star className="w-3 h-3 mr-0.5"/> Primary
                        </Badge>
                      )}
                      {a.end_date && <Badge tone="warning" size="sm">Temporary</Badge>}
                    </div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <Input placeholder="Role label (optional)" aria-label="Role label for this department" value={a.role || ""}
                              onChange={e => updateAssignment(idx, { role: e.target.value })}
                              className="h-8 text-[12px] w-[180px]"/>
                      <DatePicker value={(a.start_date || "" || '').slice(0,10)} onChange={(_v) => updateAssignment(idx, { start_date: _v })}/>
                      <DatePicker value={(a.end_date || "" || '').slice(0,10)} onChange={(_v) => updateAssignment(idx, { end_date: _v })}/>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {!a.is_primary && (
                      <Button size="sm" variant="ghost" onClick={() => setPrimary(idx)}
                              data-testid={`wiz-primary-${idx}`}>
                        <Star className="w-3.5 h-3.5 mr-1"/> Set primary
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => removeAssignment(idx)}
                            data-testid={`wiz-remove-${idx}`}>
                      <X className="w-3.5 h-3.5"/>
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-3 flex items-center gap-2">
          <Select value=""
                   onValueChange={code => {
                     const d = availableDepts.find(x => x.id === code);
                     addAssignment(d, data.department_assignments.length === 0);
                   }}>
            <SelectTrigger className="max-w-[280px]" data-testid="wiz-add-dept" aria-label="Add a department">
              <SelectValue placeholder="Add a department…"/>
            </SelectTrigger>
            <SelectContent>
              {availableDepts.length === 0 && (
                <div className="px-3 py-2 text-[12px] text-hocp-textSecondary">All departments already added.</div>
              )}
              {availableDepts.map(d => (
                <SelectItem key={d.id} value={d.id}>{d.name} ({d.code})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

/* ── Step 4 ─────────────────────────────────────────────────────────── */
function StepLogin({ data, set, templates, departments, errors = {} }) {
  const template = templates.find(t => t.id === data.access_template_id);
  const primary  = data.department_assignments.find(a => a.is_primary);
  const primaryDept = primary && departments.find(d => d.id === primary.department_id);
  return (
    <div className="space-y-4">
      <div className="hocp-card p-5">
        <div className="text-[11px] uppercase tracking-wider text-hocp-textTertiary font-semibold mb-3">
          Access Preview
        </div>
        <div className="grid grid-cols-2 gap-3 text-[13px]">
          <div>
            <div className="text-[11px] text-hocp-textTertiary">Platform Role</div>
            <div className="font-semibold">{template?.platform_role || "user"}</div>
          </div>
          <div>
            <div className="text-[11px] text-hocp-textTertiary">Access Template</div>
            <div className="font-semibold">{template?.name || "None"}</div>
          </div>
          <div>
            <div className="text-[11px] text-hocp-textTertiary">Primary Department</div>
            <div className="font-semibold">{primaryDept?.name || "Unassigned"}</div>
          </div>
          <div>
            <div className="text-[11px] text-hocp-textTertiary">Extra Permissions</div>
            <div className="font-semibold">{template?.extra_permissions?.length || 0}</div>
          </div>
        </div>
      </div>

      <div className="hocp-card p-5 space-y-3">
        <div className="text-[11px] uppercase tracking-wider text-hocp-textTertiary font-semibold">
          Login Options
        </div>
        <label className="text-[13px] flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={data.generate_temporary_password}
                  onChange={e => set({ generate_temporary_password: e.target.checked })}
                  data-testid="wiz-gen-password"/>
          <span>Generate a temporary password (shown once after creation)</span>
        </label>
        <label className="text-[13px] flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={data.force_password_change}
                  onChange={e => set({ force_password_change: e.target.checked })}
                  data-testid="wiz-force-change"/>
          <span>Force password change on first login</span>
        </label>
        <label className="text-[13px] flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={data.send_invitation_email}
                  onChange={e => set({ send_invitation_email: e.target.checked })}
                  data-testid="wiz-invite-email"/>
          <span>Queue an invitation email <span className="text-hocp-textTertiary">(sending arrives in G.4 via Resend)</span></span>
        </label>
        {!data.generate_temporary_password && (
          <Field label="Set an explicit initial password" hint="At least 12 chars — upper, lower, digit, special."
                 error={errors.initial_password} testid="fld-init-pass">
            <Input type="password" value={data.initial_password}
                    onChange={e => set({ initial_password: e.target.value })}
                    placeholder="Min 12 chars"
                    data-testid="wiz-init-pass"/>
          </Field>
        )}
      </div>
    </div>
  );
}

/* ── Step 5 ─────────────────────────────────────────────────────────── */
const ReviewRow = ({ k, v }) => (
  <div className="grid grid-cols-3 gap-4 py-2 border-b border-hocp-alice/60 last:border-b-0">
    <div className="text-[11px] uppercase tracking-wider text-hocp-textTertiary font-semibold">{k}</div>
    <div className="col-span-2 text-[13px] text-hocp-textPrimary">{v || <span className="text-hocp-textTertiary">—</span>}</div>
  </div>
);

function StepReview({ data, templates, departments, hotelName, managers, empIdPreview }) {
  const template = templates.find(t => t.id === data.access_template_id);
  const manager  = managers.find(m => m.id === data.reporting_manager_id);
  const primary  = data.department_assignments.find(a => a.is_primary);
  const primaryDept = primary && departments.find(d => d.id === primary.department_id);
  const others   = data.department_assignments
    .filter(a => !a.is_primary)
    .map(a => departments.find(d => d.id === a.department_id))
    .filter(Boolean);

  return (
    <div className="space-y-4" data-testid="wiz-review">
      <div className="rounded-2xl bg-gradient-to-br from-hocp-honeydew/15 to-hocp-vanilla/15 border border-hocp-honeydew/40 p-5">
        <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-hocp-textTertiary">You&apos;re about to onboard</div>
        <div className="text-2xl font-display font-bold text-hocp-textPrimary mt-1">{data.name}</div>
        <div className="text-[13px] text-hocp-textSecondary">{data.designation || "—"} · {template?.name || "no template"}</div>
      </div>
      <div className="hocp-card p-5">
        <ReviewRow k="Email" v={data.email}/>
        <ReviewRow k="Mobile" v={data.mobile}/>
        <ReviewRow k="Employee ID" v={data.autoGenId ? `${empIdPreview} (auto)` : data.employee_id}/>
        <ReviewRow k="Employment Type" v={data.employment_type}/>
        <ReviewRow k="Joining Date" v={data.joining_date}/>
        <ReviewRow k="Hotel" v={hotelName}/>
        <ReviewRow k="Reporting Manager" v={manager?.name || "—"}/>
        <ReviewRow k="Platform Role" v={template?.platform_role || "user"}/>
        <ReviewRow k="Access Template" v={template?.name || "None"}/>
        <ReviewRow k="Primary Department" v={primaryDept?.name}/>
        <ReviewRow k="Additional Departments" v={others.length ? others.map(d => d.name).join(", ") : "—"}/>
        <ReviewRow k="Force Password Change" v={data.force_password_change ? "Yes" : "No"}/>
      </div>
    </div>
  );
}

/* ── Success screen ─────────────────────────────────────────────────── */
function OnboardedSuccess({ result, onOpenProfile, onCreateAnother, onClose }) {
  const [copied, setCopied] = useState(null);
  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 1600);
    toast.success(`${label} copied`);
  };
  return (
    <div className="p-8 space-y-6" data-testid="wiz-success">
      <div className="text-center space-y-2">
        <div className="mx-auto w-14 h-14 rounded-full bg-hocp-success/10 flex items-center justify-center">
          <Check className="w-7 h-7 text-hocp-success" strokeWidth={2.5}/>
        </div>
        <div className="text-xl font-display font-bold text-hocp-textPrimary">Team Member Created</div>
        <div className="text-[13px] text-hocp-textSecondary">Employee ID: <span className="font-mono text-hocp-textPrimary">{result.employee_id}</span></div>
      </div>

      {result.temporary_password && (
        <div className="rounded-xl bg-hocp-vanilla/20 border border-hocp-vanilla/60 p-4"
              data-testid="wiz-tempassword-block">
          <div className="flex items-center gap-2 mb-2">
            <Info className="w-4 h-4 text-hocp-textPrimary"/>
            <div className="text-[13px] font-semibold text-hocp-textPrimary">Temporary Password — shown ONCE</div>
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 font-mono text-[13px] bg-white px-3 py-2 rounded-lg border border-hocp-border tabular-nums">
              {result.temporary_password}
            </code>
            <Button size="sm" variant="outline" onClick={() => copy(result.temporary_password, "Password")}
                    data-testid="wiz-copy-pass">
              <Copy className="w-3.5 h-3.5 mr-1"/> {copied === "Password" ? "Copied" : "Copy"}
            </Button>
          </div>
          <div className="text-[11px] text-hocp-textSecondary mt-2">
            After you dismiss this screen the password cannot be retrieved.
          </div>
        </div>
      )}

      {result.activation_link && (
        <div className="rounded-xl bg-hocp-alice/30 border border-hocp-border p-4"
              data-testid="wiz-activation-block">
          <div className="text-[11px] uppercase tracking-wider text-hocp-textTertiary font-semibold mb-2">
            Activation Link
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 font-mono text-[11px] truncate bg-white px-3 py-2 rounded-lg border border-hocp-border">
              {result.activation_link}
            </code>
            <Button size="sm" variant="outline" onClick={() => copy(result.activation_link, "Link")}
                    data-testid="wiz-copy-link">
              <Copy className="w-3.5 h-3.5 mr-1"/> {copied === "Link" ? "Copied" : "Copy"}
            </Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Button variant="outline" onClick={onCreateAnother} data-testid="wiz-create-another">Create another</Button>
        <Button onClick={() => onOpenProfile(result.team_member_id)} data-testid="wiz-open-profile">
          Open profile <ExternalLink className="w-3.5 h-3.5 ml-1"/>
        </Button>
        <Button variant="ghost" onClick={onClose} className="col-span-2" data-testid="wiz-invite-later">
          Invite later
        </Button>
      </div>

      <div className="text-center text-[11px] text-hocp-textTertiary">
        Events published: {result.events_published?.join(" · ") || "—"}
      </div>
    </div>
  );
}

/* ── main wizard ────────────────────────────────────────────────────── */
const EMPTY = {
  name: "", email: "", mobile: "", employee_id: "", autoGenId: true, photo_url: "",
  designation: "", employment_type: "PERMANENT", joining_date: "",
  primary_hotel_id: "", reporting_manager_id: "",
  access_template_id: "", apply_template_defaults: true,
  department_assignments: [],
  generate_temporary_password: true, initial_password: "",
  send_invitation_email: false, force_password_change: true,
  // real-time validation state
  emailStatus: null, mobileStatus: null,
};

export default function OnboardingWizard({ open, onClose, onCreated, templates, departments,
                                            employmentTypes }) {
  const { ctx } = useHotelContext() || {};
  const activeHotelId = ctx?.hotel_id || "";
  const activeHotelName = ctx?.hotel_name || "";
  const [step, setStep] = useState(1);
  const [completed, setCompleted] = useState(new Set());
  const [data, setData] = useState(EMPTY);
  const [empIdPreview, setEmpIdPreview] = useState("");
  const [managers, setManagers] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const set = (patch) => setData(d => ({ ...d, ...patch }));

  useEffect(() => {
    if (!open) {
      setStep(1); setCompleted(new Set()); setData(EMPTY); setErrors({}); setResult(null);
    }
  }, [open]);

  // New hires always join the hotel the user is signed in to
  useEffect(() => {
    if (open) setData(d => ({ ...d, primary_hotel_id: activeHotelId }));
  }, [open, activeHotelId]);

  // Fetch employee_id preview when hotel changes
  useEffect(() => {
    if (!data.primary_hotel_id || !data.autoGenId) return;
    api.get(`/team-members/employee-id-preview?hotel_id=${data.primary_hotel_id}`)
       .then(r => setEmpIdPreview(r.data?.preview || ""))
       .catch(() => setEmpIdPreview(""));
  }, [data.primary_hotel_id, data.autoGenId]);

  // Fetch potential managers whenever the hotel changes
  useEffect(() => {
    if (!data.primary_hotel_id) { setManagers([]); return; }
    api.get(`/team-members?hotel_id=${data.primary_hotel_id}&limit=200`)
       .then(r => setManagers(r.data || []))
       .catch(() => setManagers([]));
  }, [data.primary_hotel_id]);

  const checkAvailability = async (field, value) => {
    if (!value) return;
    try {
      const { data: r } = await api.get(`/team-members/check-availability?${field}=${encodeURIComponent(value)}`);
      set({ [`${field}Status`]: r[field] });
    } catch { /* silent */ }
  };

  const validateStep = (n) => {
    const e = {};
    if (n === 1) {
      if (!data.name?.trim()) e.name = "Name is required";
      if (!data.email?.trim()) e.email = "Email is required";
      else if (data.emailStatus === "taken") e.email = "This email is already in use";
      if (!data.autoGenId && !data.employee_id?.trim()) e.employee_id = "Enter an ID or enable Auto";
    }
    if (n === 2) {
      if (!data.primary_hotel_id) e.primary_hotel_id = "Switch to a hotel before adding a team member";
    }
    if (n === 4 && !data.generate_temporary_password) {
      const pw = data.initial_password || "";
      if (pw.length < 12) e.initial_password = "Password must be at least 12 characters";
      else if (!/[A-Z]/.test(pw) || !/[a-z]/.test(pw) || !/\d/.test(pw) || !/[^A-Za-z0-9]/.test(pw))
        e.initial_password = "Use upper & lower case letters, a number and a special character";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (!validateStep(step)) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setCompleted(c => new Set([...c, step]));
    setStep(s => Math.min(5, s + 1));
  };
  const prev = () => setStep(s => Math.max(1, s - 1));
  const jump = (id) => {
    if (id < step || completed.has(id)) setStep(id);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        name: data.name.trim(),
        email: data.email.trim(),
        mobile: data.mobile?.trim() || null,
        employee_id: data.autoGenId ? null : data.employee_id.trim(),
        photo_url: data.photo_url || null,
        designation: data.designation || null,
        employment_type: data.employment_type,
        joining_date: data.joining_date || null,
        primary_hotel_id: data.primary_hotel_id,
        reporting_manager_id: data.reporting_manager_id || null,
        access_template_id: data.access_template_id || null,
        apply_template_defaults: data.apply_template_defaults,
        department_assignments: data.department_assignments.map(a => ({
          department_id: a.department_id,
          is_primary: !!a.is_primary,
          role: a.role || null,
          start_date: a.start_date || null,
          end_date: a.end_date || null,
          reason: a.reason || "hired",
        })),
        generate_temporary_password: data.generate_temporary_password,
        initial_password: !data.generate_temporary_password ? data.initial_password : null,
        send_invitation_email: data.send_invitation_email,
        force_password_change: data.force_password_change,
      };
      const { data: r } = await api.post("/team-members/onboard", payload);
      setResult(r);
      toast.success("Team member onboarded");
      onCreated?.(r);
    } catch (e) {
      toast.error(formatApiError(e) || "Onboarding failed");
    } finally { setSubmitting(false); }
  };

  return (
    <Sheet open={open} onOpenChange={v => !v && onClose()}>
      <SheetContent side="right"
        className="p-0 !max-w-[1000px] w-[95vw] overflow-hidden flex flex-col"
        data-testid="onboarding-wizard">
        {result ? (
          <OnboardedSuccess
            result={result}
            onOpenProfile={(id) => { onClose(); onCreated?.({ ...result, openProfile: id }); }}
            onCreateAnother={() => { setResult(null); setStep(1); setCompleted(new Set()); setData(EMPTY); }}
            onClose={onClose}
          />
        ) : (
          <>
            <header className="p-5 border-b border-hocp-border bg-hocp-alice/25">
              <div className="mb-3">
                <div className="text-[11px] uppercase tracking-[0.14em] text-hocp-textTertiary font-semibold">
                  Workforce · Onboarding
                </div>
                <div className="text-lg font-display font-bold text-hocp-textPrimary">Hire a New Team Member</div>
              </div>
              <Stepper current={step} onJump={jump} completed={completed}/>
            </header>

            <main className="flex-1 overflow-y-auto p-6">
              {step === 1 && <StepBasic data={data} set={set} errors={errors}
                                          checkAvailability={checkAvailability} empIdPreview={empIdPreview}/>}
              {step === 2 && <StepEmployment data={data} set={set}
                                              employmentTypes={employmentTypes}
                                              hotelName={activeHotelName} managers={managers} errors={errors}/>}
              {step === 3 && <StepRoleDept data={data} set={set}
                                            templates={templates} departments={departments} errors={errors}/>}
              {step === 4 && <StepLogin data={data} set={set} errors={errors}
                                          templates={templates} departments={departments}/>}
              {step === 5 && <StepReview data={data} templates={templates}
                                          departments={departments} hotelName={activeHotelName}
                                          managers={managers} empIdPreview={empIdPreview}/>}
            </main>

            <footer className="p-4 border-t border-hocp-border bg-white flex items-center justify-between gap-3">
              <Button variant="outline" onClick={prev} disabled={step === 1} data-testid="wiz-back">
                <ArrowLeft className="w-4 h-4 mr-1"/> Back
              </Button>
              {step < 5 ? (
                <Button onClick={next} data-testid="wiz-next">
                  Next <ArrowRight className="w-4 h-4 ml-1"/>
                </Button>
              ) : (
                <Button onClick={submit} disabled={submitting} data-testid="wiz-submit">
                  {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : <Check className="w-4 h-4 mr-1"/>}
                  {submitting ? "Creating…" : "Create Team Member"}
                </Button>
              )}
            </footer>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
